import { Chip, Link, Stack } from '@mui/material';
import cx from 'classnames';
import { format } from 'date-fns';
import React, { memo } from 'react';
import DataFlowDetailActionsContainer from 'js/components/data_flows/DataFlowDetailActionsContainer';
import DataSetIcon from 'js/components/sidebar/icons/DataSetIcon';
import UIUtils from 'js/lib/UIUtils';
import { ReactComponent as ArrowIcon } from 'new/components/icons/arrow.svg';
import { ReactComponent as ArrowRightIcon } from 'new/components/icons/longArrowRight.svg';
import { ReactComponent as FolderIcon } from 'new/components/icons/folder.svg';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import Typography from 'new/elements/TypographySupport';
import FlowTypeIcon from './FlowTypeIcon';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import { CapsuleClusterChip } from 'new/modules/settings/capsules/components/CapsuleClusterChip/CapsuleClusterChip';
import styles from './PipelineOriginCard.module.scss';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import { ReactComponent as ErrorIcon } from 'new/components/icons/exclamation.svg';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import PipelineStatusChip from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { FlowCounters } from 'js/components/pipelines/cards/components/FlowCounters/FlowCounters';
import { ResourceCounters } from './components/ResourceCounters/ResourceCounters';
import { ProjectPipelineChip } from 'js/components/projects/chips';
const DATE_FORMAT = 'MMM dd, yyyy';

class PipelineOriginCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isClickablePartHovered: false,
      isStatusVisible: props.isPipelineSelected || false,
    };
  }

  render() {
    const { isDataSetCard } = this.props;
    if (isDataSetCard === true) {
      return this._renderSharedDataSetContent();
    } else {
      return this._renderSourceContent();
    }
  }

  _stopPropagation = (e) => {
    e.stopPropagation();
  };

  _handleExpand = () => {
    const { cardData, onExpand } = this.props;
    const { isStatusVisible } = this.state;

    this.setState((prevState) => ({
      isStatusVisible: !prevState.isStatusVisible,
    }));

    if (typeof onExpand === 'function') {
      onExpand(cardData);
    }
  };

  _handleOnMouseEnter = (e) => {
    const { isClickablePartHovered } = this.state;

    const el =
      e.target?.closest('[data-not-expanding="true"]') ||
      e.target?.dataset?.notExpanding === 'true';

    if (!el && !isClickablePartHovered) {
      this.setState({ isClickablePartHovered: true });
    } else if (el && isClickablePartHovered) {
      this.setState({ isClickablePartHovered: false });
    }
  };

  _handleOnMouseLeave = () => {
    const { isClickablePartHovered } = this.state;

    if (isClickablePartHovered) {
      this.setState({ isClickablePartHovered: false });
    }
  };

  getSharerInfo = () => {
    const { isPublic, isSharedDataSet, sharerUser, sharerOrg, ownerUser, ownerOrg } = this.props;
    let sharerInfo;

    if (isPublic) {
      sharerInfo = 'Public Nexset';
    } else if (isSharedDataSet) {
      const sharerUserLabel = sharerUser ? sharerUser.name || sharerUser.email : '';
      const sharerOrgLabel = sharerOrg
        ? sharerOrg.name || sharerOrg.email || sharerOrg.email_domain
        : '';
      sharerInfo =
        sharerUserLabel || sharerOrgLabel
          ? `Shared by ${sharerUserLabel} ${sharerOrgLabel ? `(${sharerOrgLabel})` : ''}`
          : null;
    }

    if (!isPublic && (ownerUser || ownerOrg)) {
      const ownerUserLabel = ownerUser ? ownerUser.name || ownerUser.email : '';
      const ownerOrgLabel = ownerOrg
        ? ownerOrg.name || ownerOrg.email || ownerOrg.email_domain
        : '';
      sharerInfo =
        ownerUserLabel || ownerOrgLabel
          ? `${sharerInfo ? `${sharerInfo} • ` : ''}Owned by ${ownerUserLabel} ${
              ownerOrgLabel ? `(${ownerOrgLabel})` : ''
            }`
          : sharerInfo;
    }

    return sharerInfo;
  };

  _renderSourceContent = () => {
    const { isClickablePartHovered, isStatusVisible } = this.state;
    const {
      data,
      rootFlowNode,
      childrenCount,
      sourceDataSetsCount,
      errorCount,
      connectionInfo,
      connectionOrVendorType,
      isSqlAutomation: isSQLAutomation,
      toggleCloneFlowModal,
      project,
      flowData,
      leftChildren = null,
    } = this.props;

    const errorLabel = UIUtils.toPluralFormat('Ingestion Error', errorCount, false);

    let sourceIcon;
    let sourceName;

    if (connectionInfo) {
      sourceIcon = connectionInfo.iconSm;
      sourceName = connectionInfo.name;
    } else {
      sourceName = connectionOrVendorType;
    }
    const sharerInfo = this.getSharerInfo();

    return (
      <div
        className={cx(styles.card, 'list-layout')}
        onMouseOver={this._handleOnMouseEnter}
        onMouseLeave={this._handleOnMouseLeave}
        onClick={this._handleExpand}
        data-testid={`pipeline-origin-card-${data.flow_node_id}`}
        id={`pipeline-origin-card-${data.flow_node_id}`}
      >
        <div className={styles.left}>
          {leftChildren}
          <div className={styles.leftDataBlock}>
            <Stack gap="4px">
              <Typography variant="button2" className={cx(styles.name, 'ellipsis')}>
                {rootFlowNode?.name || data.name}
              </Typography>
              <Stack
                direction="row"
                gap="16px"
                alignItems="center"
                data-testid={`pipeline-origin-card-${data.flow_node_id}-flow-id`}
              >
                {rootFlowNode?.flow_type !== 'rag' && rootFlowNode?.flow_type !== 'api_server' ? (
                  <Stack className={styles.resourceInfo}>
                    <ConnectionTypeChip
                      icon={sourceIcon}
                      label={sourceName}
                      style={{ border: 'none', padding: 0 }}
                    />
                  </Stack>
                ) : null}
                <ChipWithCopy
                  chipProps={{ 'data-not-expanding': 'true' }}
                  toCopy={data.flow_node_id}
                >
                  ID {data.flow_node_id}
                </ChipWithCopy>
                {!isSQLAutomation && (
                  <FlowTypeIcon data={data} className={styles.flowTypeContainer} />
                )}
                {isCapsuleCluster(data) && <CapsuleClusterChip name={data.cluster.name} />}
              </Stack>
            </Stack>

            {data.flow_type === 'rag' || data.flow_type === 'api_server' ? (
              <ResourceCounters
                data={data}
                flowType={data.flow_type}
                isClickablePartHovered={isClickablePartHovered}
              />
            ) : isSQLAutomation ? null : (
              <FlowCounters source={data} isClickablePartHovered={isClickablePartHovered} />
            )}
          </div>
        </div>

        <Stack direction="column" gap="16px" className={styles.right}>
          <ProjectPipelineChip id={project?.id} name={project?.name} />

          <div className={styles.innerRight}>
            <div className={styles.rightActions}>
              <Stack direction="row" gap="16px" alignItems="center">
                <PipelineStatusChip
                  id={data.flow_node_id}
                  status={data.status}
                  archivalStatus={data?.archival_status}
                  runtimeStatus={flowData?.runtime_status}
                  ingestionMode={flowData?.ingestion_mode}
                  hideInfo={!isStatusVisible}
                  showRuntimeIndicator={isStatusVisible}
                />
              </Stack>
              {errorCount > 0 && (
                <KnowledgePanel
                  trigger="hover"
                  knowledgeContent={
                    <div className="counter-holder error-counter-holder">
                      <span className="counter-label">{errorCount} </span>
                      <span className="counter-label">{errorLabel}</span>
                      <span>(in last 24 hours)</span>
                    </div>
                  }
                >
                  <div className="counters-row" style={{ marginTop: 6 }}>
                    <div className="counter-holder error-counter-holder">
                      <ErrorIcon />
                    </div>
                  </div>
                </KnowledgePanel>
              )}
              <div data-not-expanding="true">
                <DataFlowDetailActionsContainer
                  data={data}
                  projectId={project?.id}
                  inPipeline
                  toggleCloneFlowModal={toggleCloneFlowModal}
                />
              </div>
            </div>
            <div className={styles.rightCreatedAt}>
              <Typography variant="body3">
                Created on {data?.created_at ? format(new Date(data.created_at), DATE_FORMAT) : ''}
              </Typography>
              <Typography variant="body3">{sharerInfo ? sharerInfo : ''}</Typography>
            </div>
          </div>
        </Stack>
      </div>
    );
  };

  _renderSharedDataSetContent = () => {
    const { isClickablePartHovered } = this.state;
    const {
      data,
      rootFlowNode,
      childrenCount,
      errorCount,
      toggleCloneFlowModal,
      project,
      isOldDesign,
    } = this.props;
    const sharerInfo = this.getSharerInfo();

    const derivedDataSetsLabel = `${
      childrenCount.derivedDataSetsCount === 1 ? 'Nexset' : 'Nexsets'
    } Transformed`;

    const sinksLabel = childrenCount.sinksCount === 1 ? 'Destination' : 'Destinations';

    const errorLabel = UIUtils.toPluralFormat('Ingestion Error', errorCount, false);

    return (
      <div
        className={cx(styles.card, 'list-layout')}
        onMouseOver={this._handleOnMouseEnter}
        onMouseLeave={this._handleOnMouseLeave}
        onClick={this._handleExpand}
      >
        <div className={styles.left}>
          <div className={styles.leftDataBlock}>
            <Stack>
              <Typography variant="button2" className={cx(styles.name, 'ellipsis')}>
                {rootFlowNode?.name || data.name}
              </Typography>
              <Stack
                direction="row"
                spacing="16px"
                alignItems="center"
                sx={{ paddingTop: 'var(--spacing-md)' }}
              >
                <DataSetIcon />
                <ChipWithCopy
                  chipProps={{ 'data-not-expanding': 'true' }}
                  toCopy={data.flow_node_id}
                >
                  ID {data.flow_node_id}
                </ChipWithCopy>
                <FlowTypeIcon data={data} />
              </Stack>
            </Stack>
            <div className={cx(styles.counters, isClickablePartHovered && styles.countersHovered)}>
              <ArrowIcon className={styles.arrowOpen} />
              <div>
                <div className={styles.circle}>{childrenCount.derivedDataSetsCount}</div>
                <Typography variant="caption" color="primary.dark">
                  {derivedDataSetsLabel}
                </Typography>
              </div>
              <ArrowRightIcon />
              <div>
                <div className={styles.circle}>{childrenCount.sinksCount}</div>
                <Typography variant="caption" color="primary.dark">
                  {sinksLabel}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <Stack direction="column" gap="16px" className={styles.right}>
          <ProjectPipelineChip id={project?.id} name={project?.name} />

          <div className={styles.innerRight}>
            <div className={styles.rightActions}>
              <PipelineStatusChip
                id={data.flow_node_id}
                status={data.status}
                archivalStatus={data?.archival_status}
                className={styles.rightStatus}
              />

              {errorCount > 0 && (
                <KnowledgePanel
                  trigger="hover"
                  knowledgeContent={
                    <div className="counter-holder error-counter-holder">
                      <span className="counter-label">{errorCount} </span>
                      <span className="counter-label">{errorLabel}</span>
                      <span>(in last 24 hours)</span>
                    </div>
                  }
                >
                  <div className="counters-row" style={{ marginTop: 6 }}>
                    <div className="counter-holder error-counter-holder">
                      <ErrorIcon />
                    </div>
                  </div>
                </KnowledgePanel>
              )}
              <div data-not-expanding="true">
                <DataFlowDetailActionsContainer
                  data={data}
                  projectId={project?.id}
                  inPipeline={true}
                  toggleCloneFlowModal={toggleCloneFlowModal}
                />
              </div>
            </div>
            <div className={styles.rightCreatedAt}>
              <Typography variant="body3">
                Created on {data?.created_at ? format(new Date(data.created_at), DATE_FORMAT) : ''}
              </Typography>
              <Typography variant="body3">{sharerInfo ? sharerInfo : ''}</Typography>
            </div>
          </div>
        </Stack>
      </div>
    );
  };
}

// Scrolling of long lists forks without lags for me when this component memoized, not sure if it works the same way
// on low-memory devices
export default memo(PipelineOriginCard);
